import {
  DeepPartial,
  IUICfgAttributesContainerElementOptions,
  IUICfgComponentContainerElementOptions,
  IUiCfgContainer,
  IUiCfgContainerItem,
  IUICfgProductsContainerElementOptions,
  mergeUIConfigSettings,
  UICfgAttributesContainerElementOptionsDefault,
  UICfgComponentContainerElementOptionsDefault,
  UICfgProductsContainerElementOptionsDefault,
  UICfgProductsElementOptionsDefault,
  UICfgRalAttributeElementOptionsDefault,
  UICfgTextAttributeElementOptionsDefault,
} from '@canvas-logic/clp-api-types';
import { invariant } from '@canvas-logic/clp-utils';

/*
The user-defined container contains mixed types of items,
but during rendering, each type of item should have its own container
to render correctly and process some merge options.
To achieve this, the layout configuration should be processed
and additional virtual containers created when necessary.
  // TODO: for optimisation purpose should be moved to the api
*/
export const prepareUILayout = (rootContainer: IUiCfgContainer) => {
  let virtualContainersNumber = 0;
  const prepareNext = (
    container: IUiCfgContainer,
    {
      componentsSettings,
      productsSettings,
      attributesSettings,
    }: {
      componentsSettings: DeepPartial<IUICfgComponentContainerElementOptions>;
      productsSettings: DeepPartial<IUICfgProductsContainerElementOptions>;
      attributesSettings: DeepPartial<IUICfgAttributesContainerElementOptions>;
    },
  ): IUiCfgContainer => {
    const children: Array<IUiCfgContainerItem> = [];
    let lastVirtualContainerForMergeChildren: IUiCfgContainer | null = null;
    let previousChild: IUiCfgContainerItem | null = null;

    for (const child of container.children.filter((it) => it.enabled && !it.notAvailable)) {
      if (child.kind === 'container') {
        const processedContainer = prepareNext(child, {
          componentsSettings: mergeUIConfigSettings(
            child.componentsSettings,
            componentsSettings,
            UICfgComponentContainerElementOptionsDefault,
          ),
          productsSettings: mergeUIConfigSettings(
            child.productsSettings,
            productsSettings,
            UICfgProductsContainerElementOptionsDefault,
          ),
          attributesSettings: mergeUIConfigSettings(
            child.attributesSettings,
            attributesSettings,
            UICfgAttributesContainerElementOptionsDefault,
          ),
        });
        children.push(processedContainer);
        lastVirtualContainerForMergeChildren = null;
      } else {
        let newContainerProductSettings = productsSettings;
        let newContainerComponentsSettings = componentsSettings;
        let newContainerAttributesSettings = attributesSettings;
        let needNewVirtualContainer =
          !previousChild ||
          previousChild.kind !== child.kind ||
          !lastVirtualContainerForMergeChildren ||
          child.kind === 'attribute';
        if (child.kind === 'product') {
          child.uiControl = mergeUIConfigSettings(
            child.uiControl,
            lastVirtualContainerForMergeChildren?.productsSettings,
            UICfgProductsElementOptionsDefault,
          );
          needNewVirtualContainer =
            needNewVirtualContainer || !container.productsSettings.mergeProductsFromDifferentComponents;
          newContainerProductSettings = child.uiControl;
        } else if (child.kind === 'attribute') {
          let defaultAttributeSettings = UICfgAttributesContainerElementOptionsDefault;
          switch (child.type) {
            case 'RAL':
              defaultAttributeSettings = UICfgRalAttributeElementOptionsDefault;
              break;
            case 'text':
              defaultAttributeSettings = UICfgTextAttributeElementOptionsDefault;
              break;
          }
          child.uiControl = mergeUIConfigSettings(
            child.uiControl,
            lastVirtualContainerForMergeChildren?.attributesSettings,
            defaultAttributeSettings,
          );
          newContainerAttributesSettings = child.uiControl;
        } else if (child.kind === 'component') {
          child.uiControl = mergeUIConfigSettings(
            child.uiControl,
            lastVirtualContainerForMergeChildren?.componentsSettings,
            UICfgComponentContainerElementOptionsDefault,
          );
        }

        if (needNewVirtualContainer) {
          virtualContainersNumber++;
          if (
            child.kind === 'product' &&
            previousChild?.kind === 'component' &&
            child.source.componentKey === previousChild.source.componentKey
          ) {
            newContainerComponentsSettings = {
              ...newContainerComponentsSettings,
              ...previousChild.uiControl,
            };
          }
          lastVirtualContainerForMergeChildren = {
            id: `virtual-container-${virtualContainersNumber}`,
            kind: 'container',
            type:
              child.kind === 'component'
                ? 'components-container'
                : child.kind === 'product'
                ? 'products-container'
                : child.kind === 'attribute'
                ? 'attributes-container'
                : 'element-container',
            isRoot: false,
            isVirtual: true,
            name: `virtual-container-${virtualContainersNumber}`,
            children: [],
            enabled: true,
            componentsSettings: newContainerComponentsSettings,
            attributesSettings: newContainerAttributesSettings,
            productsSettings: newContainerProductSettings,
          };
          children.push(lastVirtualContainerForMergeChildren);
        }
        invariant(lastVirtualContainerForMergeChildren !== null, 'Virtual container is not created');

        lastVirtualContainerForMergeChildren.children.push(child);
      }
      previousChild = child;
    }

    return {
      ...container,
      isVirtual: false,
      children,
    };
  };
  const { attributesSettings, productsSettings, componentsSettings } = rootContainer;

  const processedLayout = prepareNext(rootContainer, {
    attributesSettings: mergeUIConfigSettings(
      attributesSettings,
      undefined,
      UICfgAttributesContainerElementOptionsDefault,
    ),
    productsSettings: mergeUIConfigSettings(productsSettings, undefined, UICfgProductsContainerElementOptionsDefault),
    componentsSettings: mergeUIConfigSettings(
      componentsSettings,
      undefined,
      UICfgComponentContainerElementOptionsDefault,
    ),
  });
  return processedLayout;
};
